import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, CheckCircle, FileSignature, ChevronUp, ChevronDown, Trash2, Plus, Save, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import type { DocumentChainStep, DocumentChainTemplate } from "@/lib/api";

interface DocumentChainBuilderProps {
  steps: DocumentChainStep[];
  onChange: (steps: DocumentChainStep[]) => void;
  templates: DocumentChainTemplate[];
  onSaveTemplate: (name: string, steps: DocumentChainStep[]) => Promise<void>;
  disabled?: boolean;
}

const STEP_ICONS: Record<DocumentChainStep["type"], React.ReactNode> = {
  notification: <Bell className="h-4 w-4 text-blue-500" />,
  approval: <CheckCircle className="h-4 w-4 text-amber-500" />,
  confirmation: <FileSignature className="h-4 w-4 text-green-500" />,
};

const ROLE_KEYS_BY_TYPE: Record<DocumentChainStep["type"], { value: DocumentChainStep["assignTo"]; labelKey: string }[]> = {
  notification: [
    { value: "employee", labelKey: "documents.chain.roleEmployee" },
    { value: "hr", labelKey: "documents.chain.roleHR" },
    { value: "line_manager", labelKey: "documents.chain.roleLineManager" },
  ],
  approval: [
    { value: "hr", labelKey: "documents.chain.roleHR" },
    { value: "line_manager", labelKey: "documents.chain.roleLineManager" },
    { value: "admin", labelKey: "documents.chain.roleAdmin" },
  ],
  confirmation: [
    { value: "employee", labelKey: "documents.chain.roleEmployee" },
  ],
};

function reorder(steps: DocumentChainStep[]): DocumentChainStep[] {
  return steps.map((s, i) => ({ ...s, order: i + 1 }));
}

export function DocumentChainBuilder({
  steps,
  onChange,
  templates,
  onSaveTemplate,
  disabled,
}: DocumentChainBuilderProps) {
  const { t } = useTranslation();
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");

  const addStep = (type: DocumentChainStep["type"]) => {
    const defaultRole = ROLE_KEYS_BY_TYPE[type][0].value;
    onChange(reorder([...steps, { order: 0, type, assignTo: defaultRole }]));
  };

  const removeStep = (index: number) => {
    onChange(reorder(steps.filter((_, i) => i !== index)));
  };

  const moveStep = (index: number, dir: -1 | 1) => {
    const next = [...steps];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(reorder(next));
  };

  const updateStep = (index: number, patch: Partial<DocumentChainStep>) => {
    onChange(reorder(steps.map((s, i) => (i === index ? { ...s, ...patch } : s))));
  };

  const loadTemplate = (template: DocumentChainTemplate) => {
    onChange(reorder(template.steps.map(s => ({ ...s }))));
  };

  const handleSaveTemplate = async () => {
    const name = templateName.trim();
    if (!name || steps.length === 0) return;
    await onSaveTemplate(name, steps);
    setTemplateName("");
    setSavingTemplate(false);
  };

  const stepTypeLabel = (type: DocumentChainStep["type"]) => {
    const key = type === "notification"
      ? "documents.chain.stepNotification"
      : type === "approval"
        ? "documents.chain.stepApproval"
        : "documents.chain.stepConfirmation";
    return t(key);
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Template loader */}
      {templates.length > 0 && (
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-muted-foreground shrink-0" />
          <Select
            disabled={disabled}
            onValueChange={(val) => {
              const tpl = templates.find(t => String(t.id) === val);
              if (tpl) loadTemplate(tpl);
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder={t("documents.chain.loadTemplate")} />
            </SelectTrigger>
            <SelectContent>
              {templates.map(tpl => (
                <SelectItem key={tpl.id} value={String(tpl.id)}>{tpl.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Step list */}
      <div className="flex flex-col gap-2 flex-1 overflow-y-auto min-h-0">
        {steps.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            {t("documents.chain.noSteps")}
          </p>
        ) : (
          steps.map((step, i) => {
            const roleOptions = ROLE_KEYS_BY_TYPE[step.type];
            const isConfirmation = step.type === "confirmation";

            return (
              <div key={i} className="rounded-lg border bg-background p-3 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="h-5 w-5 flex items-center justify-center rounded-full p-0 text-xs shrink-0">
                    {i + 1}
                  </Badge>
                  {STEP_ICONS[step.type]}
                  <span className="text-sm font-medium flex-1">{stepTypeLabel(step.type)}</span>
                  <div className="flex items-center gap-1">
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" disabled={disabled || i === 0} onClick={() => moveStep(i, -1)}>
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" disabled={disabled || i === steps.length - 1} onClick={() => moveStep(i, 1)}>
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive" disabled={disabled} onClick={() => removeStep(i)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {!isConfirmation && (
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs text-muted-foreground">{t("documents.chain.assignTo")}</Label>
                    <Select
                      disabled={disabled}
                      value={step.assignTo}
                      onValueChange={(v) => updateStep(i, { assignTo: v as DocumentChainStep["assignTo"] })}
                    >
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roleOptions.map(o => (
                          <SelectItem key={o.value} value={o.value}>{t(o.labelKey)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs text-muted-foreground">{t("documents.chain.labelOptional")}</Label>
                  <Input
                    className="h-7 text-xs"
                    placeholder={stepTypeLabel(step.type)}
                    disabled={disabled}
                    value={step.label ?? ""}
                    onChange={(e) => updateStep(i, { label: e.target.value || undefined })}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add step */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="gap-1.5 w-full" disabled={disabled}>
            <Plus className="h-3.5 w-3.5" />
            {t("documents.chain.addStep")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuItem onClick={() => addStep("notification")}>
            <Bell className="h-4 w-4 mr-2 text-blue-500" />
            <div>
              <div className="text-sm font-medium">{t("documents.chain.stepNotification")}</div>
              <div className="text-xs text-muted-foreground">{t("documents.chain.stepNotificationDesc")}</div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => addStep("approval")}>
            <CheckCircle className="h-4 w-4 mr-2 text-amber-500" />
            <div>
              <div className="text-sm font-medium">{t("documents.chain.stepApproval")}</div>
              <div className="text-xs text-muted-foreground">{t("documents.chain.stepApprovalDesc")}</div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => addStep("confirmation")}>
            <FileSignature className="h-4 w-4 mr-2 text-green-500" />
            <div>
              <div className="text-sm font-medium">{t("documents.chain.stepConfirmation")}</div>
              <div className="text-xs text-muted-foreground">{t("documents.chain.stepConfirmationDesc")}</div>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Save as template */}
      {steps.length > 0 && (
        savingTemplate ? (
          <div className="flex gap-2">
            <Input
              className="h-8 text-xs flex-1"
              placeholder={t("documents.chain.templateNamePlaceholder")}
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveTemplate(); } }}
            />
            <Button type="button" size="sm" className="h-8 px-2" onClick={handleSaveTemplate} disabled={!templateName.trim()}>
              <Save className="h-3.5 w-3.5" />
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => { setSavingTemplate(false); setTemplateName(""); }}>
              ✕
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1.5 text-xs text-muted-foreground"
            disabled={disabled}
            onClick={() => setSavingTemplate(true)}
          >
            <Save className="h-3.5 w-3.5" />
            {t("documents.chain.saveAsTemplate")}
          </Button>
        )
      )}
    </div>
  );
}
